import React from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Chip,
    IconButton,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {getEntityTypeLabel} from '../../../utils/entityTypeMeta';
import {
    getEntityTypeSuggestions,
    getFacetedPropertyOptions,
    getPropertyValueSuggestions,
    pruneStaleFilterValues,
} from '../utils/computeEntityFilterFacets';
import {
    createDefaultEntityFilters,
    createEmptyPropertyFilterRow,
    createEntityTypeFilterRow,
} from '../utils/evaluateEntityPropertyFilters';

function propertyOptionLabel(option) {
    if (typeof option === 'string') return option;
    return option?.label || option?.propertyKey || '';
}

function propertyKeyFromValue(value) {
    if (value == null) return '';
    if (typeof value === 'string') return value.trim();
    return (value.propertyKey || '').trim();
}

function MultivalueChipAutocomplete({
    label,
    placeholder,
    options,
    values,
    onChange,
    getOptionLabel = (option) => option,
    isOptionEqualToValue = (option, value) => option === value,
    renderOption,
    sx,
}) {
    const selectedOptions = values.map((value) => {
        const match = options.find((option) => isOptionEqualToValue(option, value));
        return match ?? value;
    });

    return (
        <Autocomplete
            multiple
            size="small"
            options={options}
            value={selectedOptions}
            filterSelectedOptions
            disableClearable
            onChange={(_, newValue) => {
                onChange(newValue);
            }}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={isOptionEqualToValue}
            renderOption={renderOption}
            renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => {
                    const {key, ...tagProps} = getTagProps({index});
                    return (
                        <Chip
                            {...tagProps}
                            key={key}
                            size="small"
                            label={getOptionLabel(option)}
                        />
                    );
                })
            }
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    placeholder={placeholder}
                />
            )}
            sx={{flex: 1, minWidth: 0, ...sx}}
        />
    );
}

function EntityTypeFilterInput({row, suggestions, onValuesChange, sx}) {
    const options = suggestions.map((option) => option.token);
    const values = Array.isArray(row.values) ? row.values : [];

    return (
        <MultivalueChipAutocomplete
            label="Entity type"
            placeholder="Add type…"
            options={options}
            values={values}
            onChange={onValuesChange}
            getOptionLabel={(token) => getEntityTypeLabel(token) || token}
            isOptionEqualToValue={(option, value) => (
                String(option).toLowerCase() === String(value).toLowerCase()
            )}
            renderOption={(props, token) => {
                const {key, ...optionProps} = props;
                return (
                    <li key={key || token} {...optionProps}>
                        {getEntityTypeLabel(token) || token}
                    </li>
                );
            }}
            sx={sx}
        />
    );
}

function PropertyFilterRow({
    row,
    propertyOptions,
    valueSuggestions,
    onPropertyChange,
    onValuesChange,
    onRemove,
}) {
    const selectedOption = propertyOptions.find(
        (option) => option.propertyKey === row.propertyKey,
    );
    const propertyValue = selectedOption
        || (row.propertyKey ? {propertyKey: row.propertyKey, label: row.propertyKey} : null);
    const values = Array.isArray(row.values) ? row.values : [];

    return (
        <Stack direction="row" spacing={1} alignItems="flex-start">
            <Autocomplete
                size="small"
                options={propertyOptions}
                value={propertyValue}
                getOptionLabel={propertyOptionLabel}
                isOptionEqualToValue={(option, value) => (
                    propertyKeyFromValue(option) === propertyKeyFromValue(value)
                )}
                filterOptions={(options, state) => {
                    const query = state.inputValue.trim().toLowerCase();
                    if (!query) return options;
                    return options.filter((option) => (
                        (option.label || '').toLowerCase().includes(query)
                        || (option.propertyKey || '').toLowerCase().includes(query)
                    ));
                }}
                onChange={(_, newValue) => {
                    onPropertyChange(propertyKeyFromValue(newValue));
                }}
                renderOption={(props, option) => {
                    const {key, ...optionProps} = props;
                    return (
                        <li key={key || option.propertyKey} {...optionProps}>
                            <Typography variant="body2" sx={{py: 0.25}}>
                                {option.label || option.propertyKey}
                            </Typography>
                        </li>
                    );
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Property"
                        placeholder="Property"
                    />
                )}
                sx={{flex: 1.35, minWidth: 0}}
            />

            <MultivalueChipAutocomplete
                label="Value"
                placeholder="Add value…"
                options={valueSuggestions}
                values={values}
                onChange={onValuesChange}
                getOptionLabel={(option) => option}
                isOptionEqualToValue={(option, value) => (
                    String(option).toLowerCase() === String(value).toLowerCase()
                )}
            />

            <IconButton
                size="small"
                aria-label="Remove filter"
                onClick={onRemove}
                sx={{flexShrink: 0, mt: 0.5}}
            >
                <DeleteOutlineIcon fontSize="small"/>
            </IconButton>
        </Stack>
    );
}

/**
 * Expandable property filter rows for the Whiteboard Entities tab.
 * Operator is always '=' (equals); the UI does not expose an Op control.
 * Max distance is handled by ChartFacetChipBar (Zone 4 slider).
 */
export default function EntityPropertyFilterPanel({
    filters = [],
    onFiltersChange,
    propertyOptions = [],
    chartEntities = [],
    propertiesByEntityId = {},
    summariesByEntityId = {},
    summariesLoading = false,
}) {
    const entityTypeRow = React.useMemo(
        () => filters.find((row) => row.kind === 'entityType') || createEntityTypeFilterRow(),
        [filters],
    );
    const propertyRows = React.useMemo(
        () => filters.filter((row) => row.kind !== 'entityType'),
        [filters],
    );

    React.useEffect(() => {
        const pruned = pruneStaleFilterValues(
            filters,
            chartEntities,
            propertiesByEntityId,
            summariesByEntityId,
            {summariesLoading},
        );
        const changed = pruned.length !== filters.length || pruned.some((row, index) => {
            const prev = filters[index];
            if (prev?.id !== row?.id) return true;
            const prevValues = Array.isArray(prev?.values) ? prev.values : [];
            const nextValues = Array.isArray(row?.values) ? row.values : [];
            return prevValues.join('\0') !== nextValues.join('\0');
        });
        if (changed) {
            onFiltersChange?.(pruned);
        }
    }, [filters, chartEntities, propertiesByEntityId, summariesByEntityId, summariesLoading, onFiltersChange]);

    const entityTypeSuggestions = React.useMemo(
        () => getEntityTypeSuggestions(
            chartEntities,
            propertiesByEntityId,
            summariesByEntityId,
            filters,
            entityTypeRow.id,
            {summariesLoading},
        ),
        [chartEntities, propertiesByEntityId, summariesByEntityId, summariesLoading, filters, entityTypeRow.id],
    );

    const updateEntityTypeValues = React.useCallback((values) => {
        const next = filters.some((row) => row.kind === 'entityType')
            ? filters.map((row) => (
                row.kind === 'entityType' ? {...row, values} : row
            ))
            : [{...entityTypeRow, values}, ...filters];
        onFiltersChange?.(next);
    }, [filters, entityTypeRow, onFiltersChange]);

    const updatePropertyRow = React.useCallback((rowId, patch) => {
        onFiltersChange?.(filters.map((row) => (
            row.id === rowId ? {...row, kind: 'property', operator: '=', ...patch} : row
        )));
    }, [filters, onFiltersChange]);

    const removePropertyRow = React.useCallback((rowId) => {
        const remainingPropertyRows = propertyRows.filter((row) => row.id !== rowId);
        const next = [
            entityTypeRow,
            ...(remainingPropertyRows.length > 0 ? remainingPropertyRows : [createEmptyPropertyFilterRow()]),
        ];
        onFiltersChange?.(next);
    }, [entityTypeRow, propertyRows, onFiltersChange]);

    const addPropertyRow = React.useCallback(() => {
        onFiltersChange?.([...filters, createEmptyPropertyFilterRow()]);
    }, [filters, onFiltersChange]);

    const clearFilters = React.useCallback(() => {
        onFiltersChange?.(createDefaultEntityFilters());
    }, [onFiltersChange]);

    return (
        <Stack spacing={1.5} sx={{px: 2, pb: 1.5}}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
                <EntityTypeFilterInput
                    row={entityTypeRow}
                    suggestions={entityTypeSuggestions}
                    onValuesChange={updateEntityTypeValues}
                    sx={{flex: 1, minWidth: 0}}
                />
            </Stack>

            {propertyRows.map((row) => {
                const facetedPropertyOptions = getFacetedPropertyOptions(
                    chartEntities,
                    propertiesByEntityId,
                    summariesByEntityId,
                    filters,
                    row.id,
                    propertyOptions,
                    {summariesLoading},
                );
                const valueSuggestions = getPropertyValueSuggestions(
                    chartEntities,
                    propertiesByEntityId,
                    summariesByEntityId,
                    filters,
                    row.id,
                    row.propertyKey,
                    {summariesLoading},
                );

                return (
                    <PropertyFilterRow
                        key={row.id}
                        row={row}
                        propertyOptions={facetedPropertyOptions}
                        valueSuggestions={valueSuggestions}
                        onPropertyChange={(propertyKey) => updatePropertyRow(row.id, {
                            propertyKey,
                            values: [],
                        })}
                        onValuesChange={(values) => updatePropertyRow(row.id, {values})}
                        onRemove={() => removePropertyRow(row.id)}
                    />
                );
            })}

            <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
            >
                <Button
                    size="small"
                    startIcon={<AddIcon/>}
                    onClick={addPropertyRow}
                    sx={{whiteSpace: 'nowrap'}}
                >
                    Add filter
                </Button>
                <Button
                    size="small"
                    color="inherit"
                    onClick={clearFilters}
                    sx={{whiteSpace: 'nowrap'}}
                >
                    Clear filters
                </Button>
                {propertyOptions.length === 0 && entityTypeSuggestions.length === 0 && (
                    <Typography variant="caption" color="text.secondary" component={Box}>
                        No KB properties on chart entities yet.
                    </Typography>
                )}
            </Stack>
        </Stack>
    );
}

export {
    createDefaultEntityFilters,
    createEmptyPropertyFilterRow,
    createEntityTypeFilterRow,
    createEmptyPropertyFilterRow as createEmptyFilterRow,
};
